import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Cloud as CloudIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../models/AuthContext';
import adminSettingsService from '../models/AdminSettingsService';

const NetatmoAccountsManager = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, account: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [testingAccount, setTestingAccount] = useState(null);
  const [defaultStation, setDefaultStation] = useState(null);
  const [savingDefaultStation, setSavingDefaultStation] = useState(false);

  // Charger les comptes au montage
  useEffect(() => {
    loadAccounts();
    loadDefaultStation();
  }, []);

  // Charger la station par défaut
  const loadDefaultStation = async () => {
    try {
      const stationId = await adminSettingsService.getDefaultStation();
      setDefaultStation(stationId);
    } catch (error) {
      console.error('Erreur chargement station par défaut:', error);
    }
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const listAccounts = httpsCallable(functions, 'netatmoListAccounts');
      const result = await listAccounts();
      setAccounts(result.data.accounts);
    } catch (error) {
      console.error('Erreur chargement comptes:', error);
      showSnackbar('Erreur lors du chargement des comptes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async () => {
    try {
      setLoading(true);
      const initAuth = httpsCallable(functions, 'netatmoInitAuth');
      const result = await initAuth();
      
      // Détecter Safari et iOS
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isMacOS = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      
      if (isIOS) {
        // Pour iOS, toujours utiliser une redirection (pas de popup possible)
        showSnackbar('Redirection vers Netatmo pour l\'authentification...', 'info');
        sessionStorage.setItem('netatmo-return-url', window.location.href);
        
        setTimeout(() => {
          window.location.href = result.data.authUrl;
        }, 1500);
      } else if (isSafari && isMacOS) {
        // Pour Safari sur macOS, essayer d'ouvrir dans un nouvel onglet
        const authTab = window.open(result.data.authUrl, '_blank');
        
        if (!authTab || authTab.closed) {
          // Si bloqué, fallback sur redirection
          showSnackbar('Les popups sont bloqués. Redirection vers Netatmo...', 'warning');
          sessionStorage.setItem('netatmo-return-url', window.location.href);
          
          setTimeout(() => {
            window.location.href = result.data.authUrl;
          }, 2000);
        } else {
          // L'onglet s'est ouvert, on peut attendre son retour
          showSnackbar('Authentification en cours dans le nouvel onglet...', 'info');
          setLoading(false); // Libérer l'interface
          
          // Recharger périodiquement pour vérifier si l'auth est terminée
          const reloadInterval = setInterval(() => {
            loadAccounts();
          }, 3000);
          
          // Arrêter après 2 minutes
          setTimeout(() => {
            clearInterval(reloadInterval);
            showSnackbar('Vérification terminée. Rafraîchissez si nécessaire.', 'info');
          }, 120000);
        }
      } else {
        // Pour les autres navigateurs, utiliser window.open
        const authWindow = window.open(result.data.authUrl, 'netatmo-auth', 'width=600,height=700');
        
        if (!authWindow || authWindow.closed || typeof authWindow.closed === 'undefined') {
          // Si le popup est bloqué, fallback sur la redirection
          console.warn('Popup bloqué, utilisation de la redirection');
          sessionStorage.setItem('netatmo-return-url', window.location.href);
          window.location.href = result.data.authUrl;
        } else {
          // Popup ouvert avec succès
          setLoading(false);
          showSnackbar('Authentification en cours dans la fenêtre popup...', 'info');
          
          // Écouter le retour
          const checkInterval = setInterval(() => {
            try {
              if (authWindow.closed) {
                clearInterval(checkInterval);
                // Recharger les comptes après fermeture
                setTimeout(() => {
                  loadAccounts();
                  checkUrlParams();
                }, 1000);
              }
            } catch (e) {
              clearInterval(checkInterval);
            }
          }, 1000);
        }
      }
      
    } catch (error) {
      console.error('Erreur ajout compte:', error);
      showSnackbar('Erreur lors de l\'ajout du compte', 'error');
      setLoading(false);
    }
  };

  // Vérifier les paramètres URL au retour OAuth
  const checkUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('netatmo_success') === 'true') {
      showSnackbar('Compte Netatmo ajouté avec succès !', 'success');
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Recharger les comptes après succès
      loadAccounts();
      
      // Si on avait sauvegardé une URL de retour (Safari/iOS), la supprimer
      sessionStorage.removeItem('netatmo-return-url');
    }
    
    if (urlParams.get('netatmo_error')) {
      showSnackbar(`Erreur: ${urlParams.get('netatmo_error')}`, 'error');
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Supprimer l'URL de retour en cas d'erreur aussi
      sessionStorage.removeItem('netatmo-return-url');
    }
    
    // Si on a des paramètres OAuth dans l'URL (code, state)
    if (urlParams.get('code') && urlParams.get('state')) {
      // Le callback OAuth a été géré côté serveur
      // On peut nettoyer l'URL et recharger
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        loadAccounts();
      }, 1000);
    }
  };

  useEffect(() => {
    checkUrlParams();
  }, []);

  const handleTestAccount = async (accountId) => {
    setTestingAccount(accountId);
    try {
      const testAccount = httpsCallable(functions, 'netatmoTestAccount');
      const result = await testAccount({ accountId });
      
      if (result.data.success) {
        showSnackbar(
          result.data.refreshed 
            ? 'Connexion OK (tokens rafraîchis)' 
            : 'Connexion OK', 
          'success'
        );
        if (result.data.refreshed) {
          loadAccounts(); // Recharger pour voir les nouveaux tokens
        }
      } else {
        showSnackbar(`Test échoué: ${result.data.message}`, 'error');
        if (result.data.needsReauth) {
          loadAccounts();
        }
      }
    } catch (error) {
      console.error('Erreur test compte:', error);
      showSnackbar('Erreur lors du test', 'error');
    } finally {
      setTestingAccount(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteDialog.account) return;
    
    try {
      setLoading(true);
      const removeAccount = httpsCallable(functions, 'netatmoRemoveAccount');
      await removeAccount({ accountId: deleteDialog.account.id });
      
      showSnackbar('Compte supprimé avec succès', 'success');
      setDeleteDialog({ open: false, account: null });
      loadAccounts();
    } catch (error) {
      console.error('Erreur suppression compte:', error);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleDefaultStationChange = async (stationId) => {
    try {
      setSavingDefaultStation(true);
      await adminSettingsService.setDefaultStation(stationId);
      setDefaultStation(stationId);
      showSnackbar('Station par défaut mise à jour', 'success');
    } catch (error) {
      console.error('Erreur sauvegarde station par défaut:', error);
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSavingDefaultStation(false);
    }
  };

  const getStatusChip = (account) => {
    if (account.needsReauth) {
      return <Chip label="Ré-auth requise" color="error" size="small" icon={<ErrorIcon />} />;
    }
    
    const expiresAt = new Date(account.tokenExpiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expiresAt - now) / (1000 * 60 * 60);
    
    if (hoursUntilExpiry < 1) {
      return <Chip label="Expire bientôt" color="warning" size="small" icon={<WarningIcon />} />;
    }
    
    return <Chip label="Actif" color="success" size="small" icon={<CheckIcon />} />;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Vérification de l'authentification
  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Vous devez être connecté pour gérer les comptes Netatmo.
      </Alert>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h2">
              Comptes Netatmo
            </Typography>
            <Box>
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadAccounts}
                disabled={loading}
                sx={{ mr: 2 }}
              >
                Actualiser
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddAccount}
                disabled={loading}
              >
                Ajouter un compte
              </Button>
            </Box>
          </Box>

          {loading && !accounts.length ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : accounts.length === 0 ? (
            <Alert severity="info">
              Aucun compte Netatmo configuré. Cliquez sur "Ajouter un compte" pour commencer.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Station</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Localisation</TableCell>
                    <TableCell>État</TableCell>
                    <TableCell>Dernière synchro</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CloudIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {account.accountName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {account.stationName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{account.email}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.metadata?.location?.city || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.metadata?.location?.country || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(account)}</TableCell>
                      <TableCell>{formatDate(account.lastRefresh)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Tester la connexion">
                          <IconButton
                            onClick={() => handleTestAccount(account.id)}
                            disabled={testingAccount === account.id}
                            color="primary"
                          >
                            {testingAccount === account.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <RefreshIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            onClick={() => setDeleteDialog({ open: true, account })}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {accounts.some(a => a.needsReauth) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Certains comptes nécessitent une ré-authentification. 
              Supprimez et ajoutez à nouveau ces comptes.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Card de configuration station par défaut */}
      {accounts.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6" component="h3">
                Station par défaut
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Sélectionnez la station qui sera affichée par défaut dans le dashboard pluie.
              Vous pourrez toujours basculer en mode comparaison pour voir toutes les stations.
            </Alert>

            <FormControl fullWidth disabled={savingDefaultStation}>
              <InputLabel>Station par défaut</InputLabel>
              <Select
                value={defaultStation || ''}
                onChange={(e) => handleDefaultStationChange(e.target.value)}
                label="Station par défaut"
              >
                {accounts.filter(a => !a.needsReauth).map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box display="flex" alignItems="center" width="100%">
                      <CloudIcon sx={{ mr: 1, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2">
                          {account.stationName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.metadata?.location?.city || account.email}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {savingDefaultStation && (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={20} />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, account: null })}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le compte "{deleteDialog.account?.accountName}" ?
            Cette action est irréversible et supprimera également toutes les données en cache.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, account: null })}>
            Annuler
          </Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NetatmoAccountsManager;